import {BaseCommand} from './base.command';
import {SmapTextChangeSnapshot, UndoableCommand} from '../interfaces/command.interface';
import {MapService} from '../../../api/services/map.service';
import {firstValueFrom} from 'rxjs';
import {UndoLabelMetadata} from '../undo-label.utils';

/**
 * SmapTextChangeCommand handles map name and description changes.
 * Supports text coalescing within a focus session.
 */
export class SmapTextChangeCommand extends BaseCommand<SmapTextChangeSnapshot> {
  private focusSessionId: string;
  private lastUpdateTime: number;

  constructor(
    private mapService: MapService,
    snapshot: SmapTextChangeSnapshot,
    focusSessionId: string
  ) {
    // Map field to display name
    const partName = snapshot.field === 'name' ? 'Name' : 'Description';

    const labelMetadata: UndoLabelMetadata = {
      object: 'Smap',
      verb: 'Change',
      part: partName
    };

    super(
      'smap-text-change',
      `Smap ${partName} Change`,
      {before: snapshot, after: {...snapshot}},
      labelMetadata
    );
    this.focusSessionId = focusSessionId;
    this.lastUpdateTime = Date.now();
  }

  async execute(): Promise<void> {
    const {mapId, field, after} = this.snapshot.after;

    // Get full map from store first, then update the field
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (currentMap && currentMap.id === mapId) {
      currentMap[field] = after;

      // Update on backend with FULL map object
      await firstValueFrom(
        this.mapService.update({...currentMap})
      );

      // Update local store
      this.mapService.setCurrentSelectedMapToStore({...currentMap});

      if (field === 'name') {
        this.mapService.setMapNameSignal(after);
      }
    }
  }

  async undo(): Promise<void> {
    const {mapId, field, before} = this.snapshot.before;

    // Get full map from store first, then restore the field
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (currentMap && currentMap.id === mapId) {
      currentMap[field] = before;

      // Restore previous value on backend with FULL map object
      await firstValueFrom(
        this.mapService.update({...currentMap})
      );

      // Update local store
      this.mapService.setCurrentSelectedMapToStore({...currentMap});

      if (field === 'name') {
        this.mapService.setMapNameSignal(before);
      }
    }
  }

  /**
   * Check if this command can be merged with another.
   * Commands can be merged if they're for the same map, field, and focus session.
   */
  override canMerge(other: UndoableCommand): boolean {
    if (!(other instanceof SmapTextChangeCommand)) return false;
    if (other.focusSessionId !== this.focusSessionId) return false;
    if (other.snapshot.before.mapId !== this.snapshot.before.mapId) return false;
    if (other.snapshot.before.field !== this.snapshot.before.field) return false;
    return true;
  }

  /**
   * Merge another text change command into this one.
   * Keeps the original "before" value but updates the "after" value.
   */
  override merge(other: UndoableCommand): void {
    if (!(other instanceof SmapTextChangeCommand)) return;

    // Keep original 'before', update 'after' to the latest value
    this.snapshot.after.after = other.snapshot.after.after;
    this.lastUpdateTime = other.lastUpdateTime;
  }
}
